import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, TableShell, Textarea } from "@/components/admin/AdminUI";
import { MediaUpload } from "@/components/admin/MediaUpload";
import { DownloadUpload } from "@/components/admin/DownloadUpload";
import { BulkMediaTable } from "@/components/admin/BulkMediaTable";
import { listAllOrders, markOrderShipped } from "@/lib/shop.functions";
import { ProductAssetsPanel } from "@/components/admin/ProductAssetsPanel";

export const Route = createFileRoute("/_authenticated/admin/shop")({
  component: ShopAdmin,
});

type P = Database["public"]["Tables"]["shop_products"]["Row"];
type Ins = Database["public"]["Tables"]["shop_products"]["Insert"];

const KINDS = ["digital", "album", "book", "bundle", "physical", "garment"] as const;

const EMPTY: Ins = {
  slug: "", title: "", subtitle: "", long_description: "",
  image_url: "", kind: "digital", amount_cents: 0, currency: "usd",
  active: true, sort_order: 0, external_purchase_url: "",
  requires_shipping: false, shipping_amount_cents: 0,
  tax_code: "txcd_10000000", download_asset_path: "",
};

type OrderRow = {
  id: string;
  product_slug: string;
  product_title: string;
  amount_cents: number;
  currency: string;
  status: string;
  environment: string;
  fulfillment_status: string;
  tracking_number: string | null;
  tracking_url: string | null;
  shipping_name: string | null;
  shipping_address: unknown;
  customer_email: string | null;
  created_at: string;
};

function ShopAdmin() {
  const [rows, setRows] = useState<P[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<P | "new" | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [fulfillOrder, setFulfillOrder] = useState<OrderRow | null>(null);

  async function load() {
    setLoading(true);
    const [{ data }, o] = await Promise.all([
      supabase.from("shop_products").select("*").order("sort_order").order("created_at"),
      listAllOrders().catch(() => [] as OrderRow[]),
    ]);
    setRows(data ?? []);
    setOrders((o as OrderRow[]) ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("shop_products").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  function fmt(cents: number, currency: string) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  }

  return (
    <>
      <AdminSection
        title="Shop products"
        description="Physical and digital artifacts. Digital products need a download asset; physical products need a shipping cost."
        action={<AdminButton onClick={() => setEditing("new")}>+ New product</AdminButton>}
      >
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
         rows.length === 0 ? <p className="text-sm text-muted-foreground">No products yet.</p> :
          <TableShell
            headers={["", "Product", "Kind", "Price", "Ship?", "Status", ""]}
            rows={rows.map((r) => (
              <tr key={r.id} className="hover:bg-obsidian/40">
                <td className="px-4 py-3">
                  {r.image_url ? <img src={r.image_url} alt="" className="h-10 w-10 rounded object-cover" loading="lazy" decoding="async" /> : <div className="h-10 w-10 rounded border hairline" />}
                </td>
                <td className="px-4 py-3">
                  <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                  <div className="text-xs text-muted-foreground">/{r.slug}</div>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">{r.kind}</td>
                <td className="px-4 py-3 text-sm">{r.external_purchase_url ? "External" : fmt(r.amount_cents, r.currency)}</td>
                <td className="px-4 py-3 text-xs">{r.requires_shipping ? `+${fmt(r.shipping_amount_cents ?? 0, r.currency)}` : "digital"}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={r.active ? "text-emerald" : "text-muted-foreground"}>{r.active ? "Live" : "Hidden"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                    <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                  </div>
                </td>
              </tr>
            ))}
          />
        }
      </AdminSection>

      <AdminSection
        title="Orders"
        description="All shop orders across sandbox and live. Mark physical orders shipped to notify the customer."
      >
        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <TableShell
            headers={["Date", "Product", "Buyer", "Amount", "Status", "Fulfillment", ""]}
            rows={orders.map((o) => (
              <tr key={o.id} className="hover:bg-obsidian/40">
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="font-serif text-sm text-gold-luminous">{o.product_title}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">/{o.product_slug}</div>
                </td>
                <td className="px-4 py-3 text-xs">{o.customer_email ?? "—"}{o.environment === "sandbox" && <span className="ml-1 text-muted-foreground">· test</span>}</td>
                <td className="px-4 py-3 text-sm">{fmt(o.amount_cents, o.currency)}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">{o.status}</td>
                <td className="px-4 py-3 text-xs">
                  {o.shipping_address ? (
                    <span>{o.fulfillment_status}{o.tracking_number ? ` · ${o.tracking_number}` : ""}</span>
                  ) : (
                    <span className="text-muted-foreground">digital</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {!!o.shipping_address && o.status === "paid" && o.fulfillment_status !== "delivered" && (
                      <AdminButton variant="ghost" onClick={() => setFulfillOrder(o)}>Fulfill</AdminButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          />
        )}
      </AdminSection>

      <AdminSection
        title="Bulk media"
        description="Attach cover images and download files to many products in one session. Each upload saves the product as soon as it finishes."
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Create products first, then bulk-attach their media here.</p>
        ) : (
          <BulkMediaTable products={rows} onSaved={load} />
        )}
      </AdminSection>

      {editing && <Form initial={editing === "new" ? EMPTY : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {fulfillOrder && <FulfillDialog order={fulfillOrder} onClose={() => setFulfillOrder(null)} onSaved={() => { setFulfillOrder(null); load(); }} />}
    </>
  );
}

function Form({ initial, onClose, onSaved }: { initial: P | Ins; onClose: () => void; onSaved: () => void }) {
  const isNew = !("id" in initial);
  const [f, setF] = useState({
    slug: initial.slug ?? "",
    title: initial.title ?? "",
    subtitle: initial.subtitle ?? "",
    long_description: initial.long_description ?? "",
    image_url: initial.image_url ?? "",
    kind: initial.kind ?? "digital",
    amount_cents: (initial.amount_cents ?? 0).toString(),
    currency: initial.currency ?? "usd",
    active: initial.active ?? true,
    sort_order: (initial.sort_order ?? 0).toString(),
    external_purchase_url: initial.external_purchase_url ?? "",
    requires_shipping: initial.requires_shipping ?? false,
    shipping_amount_cents: (initial.shipping_amount_cents ?? 0).toString(),
    tax_code: initial.tax_code ?? "txcd_10000000",
    download_asset_path: initial.download_asset_path ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    const payload: Ins = {
      slug: f.slug.trim(),
      title: f.title.trim(),
      subtitle: f.subtitle.trim() || null,
      long_description: f.long_description,
      image_url: f.image_url.trim() || null,
      kind: f.kind,
      amount_cents: parseInt(f.amount_cents, 10) || 0,
      currency: f.currency.trim().toLowerCase() || "usd",
      active: f.active,
      sort_order: parseInt(f.sort_order, 10) || 0,
      external_purchase_url: f.external_purchase_url.trim() || null,
      requires_shipping: f.requires_shipping,
      shipping_amount_cents: parseInt(f.shipping_amount_cents, 10) || 0,
      tax_code: f.tax_code.trim() || "txcd_10000000",
      download_asset_path: f.download_asset_path.trim() || null,
    };
    const q = isNew
      ? supabase.from("shop_products").insert(payload)
      : supabase.from("shop_products").update(payload).eq("id", (initial as P).id);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New product" : `Edit · ${initial.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Slug"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></Field>
        </Row>
        <Field label="Subtitle"><Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} /></Field>
        <Field label="Long description"><Textarea rows={5} value={f.long_description} onChange={(e) => setF({ ...f, long_description: e.target.value })} /></Field>
        <Field label="Cover image">
          <MediaUpload value={f.image_url} onChange={(url) => setF({ ...f, image_url: url })} folder="shop" />
        </Field>
        <Row>
          <Field label="Kind">
            <select
              value={f.kind}
              onChange={(e) => setF({ ...f, kind: e.target.value })}
              className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm focus:border-gold"
            >
              {KINDS.map((k) => <option key={k} value={k} className="bg-obsidian">{k}</option>)}
            </select>
          </Field>
          <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Amount (cents)" hint="e.g. 2500 = $25.00. Leave 0 if external."><Input type="number" min={0} value={f.amount_cents} onChange={(e) => setF({ ...f, amount_cents: e.target.value })} /></Field>
          <Field label="Currency" hint="ISO code, lowercase (usd, eur, gbp…)"><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} /></Field>
        </Row>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.requires_shipping} onChange={(e) => setF({ ...f, requires_shipping: e.target.checked })} />
          <span>Physical product — requires shipping</span>
        </label>
        {f.requires_shipping && (
          <Field label="Shipping amount (cents)" hint="Flat rate applied at checkout"><Input type="number" min={0} value={f.shipping_amount_cents} onChange={(e) => setF({ ...f, shipping_amount_cents: e.target.value })} /></Field>
        )}
        <Field label="Stripe tax code" hint="txcd_10000000 = digital goods · txcd_20030000 = physical · txcd_35010000 = books">
          <Input value={f.tax_code} onChange={(e) => setF({ ...f, tax_code: e.target.value })} />
        </Field>
        {!f.requires_shipping && (
          <>
            <Field label="Primary download asset (legacy · single-file products)" hint="MP3, PDF, EPUB, or ZIP. Used when no multi-file assets are attached below. Buyers receive a signed URL after purchase.">
              <DownloadUpload
                value={f.download_asset_path}
                onChange={(path) => setF({ ...f, download_asset_path: path })}
                folder={f.kind === "album" || f.kind === "digital" ? "music" : f.kind === "book" ? "books" : f.kind}
              />
            </Field>
            {!isNew && (
              <div className="rounded-lg border hairline bg-obsidian/40 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Multi-file assets</p>
                <p className="mt-1 text-xs text-muted-foreground">For albums, PDF + EPUB pairs, wallpaper packs, video courses, and download bundles. When any assets exist here, buyers see the full list on their Library card and the legacy field above is ignored.</p>
                <ProductAssetsPanel productSlug={f.slug || initial.slug || ""} defaultFolder={f.kind === "album" || f.kind === "digital" ? "music" : f.kind === "book" ? "books" : f.kind} />
              </div>
            )}
          </>
        )}
        <Field label="External purchase URL" hint="If set, checkout is bypassed and buyers are sent to this URL.">
          <Input type="url" value={f.external_purchase_url} onChange={(e) => setF({ ...f, external_purchase_url: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
          <span>Live in the shop</span>
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : isNew ? "Create" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}

function FulfillDialog({ order, onClose, onSaved }: { order: OrderRow; onClose: () => void; onSaved: () => void }) {
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number ?? "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url ?? "");
  const [status, setStatus] = useState<"shipped" | "delivered">(order.fulfillment_status === "delivered" ? "delivered" : "shipped");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await markOrderShipped({
        data: {
          orderId: order.id,
          trackingNumber: trackingNumber.trim() || undefined,
          trackingUrl: trackingUrl.trim() || undefined,
          fulfillmentStatus: status,
        },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally { setBusy(false); }
  }

  const addr = order.shipping_address as { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string } | null;

  return (
    <AdminDialog open onClose={onClose} title={`Fulfill · ${order.product_title}`}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-lg border hairline bg-obsidian/40 p-3 text-xs">
          <div className="font-serif text-sm text-gold-luminous">{order.shipping_name ?? order.customer_email}</div>
          {addr && (
            <div className="mt-1 text-muted-foreground">
              {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
              {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}<br />
              {addr.country}
            </div>
          )}
        </div>
        <Row>
          <Field label="Tracking number"><Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} /></Field>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as "shipped" | "delivered")} className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm focus:border-gold">
              <option value="shipped" className="bg-obsidian">Shipped</option>
              <option value="delivered" className="bg-obsidian">Delivered</option>
            </select>
          </Field>
        </Row>
        <Field label="Tracking URL"><Input type="url" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} /></Field>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : "Mark " + status}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}
